import { ManifestChildSetting, ManifestSetting, ResolveConfigOverrides } from './types';

const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Longest single chip, in characters — the backend's `CHIP_MAX_LENGTH`
 * (`Resolve/resolve_admin.py`). `ChipsInput` caps its add-input at it, and `chipsError`
 * flags a stored item over it. */
export const CHIP_MAX_LENGTH = 100;

/** Size caps for free-text settings — the backend's `TEXT_MAX_LENGTH` / `TEXTAREA_MAX_LENGTH` /
 * `ASSET_MAX_LENGTH` (`Resolve/resolve_admin.py`). The public runtime route serves every stored
 * value to storefronts, so nothing is unbounded. */
export const TEXT_MAX_LENGTH = 500;
export const TEXTAREA_MAX_LENGTH = 10000;
export const ASSET_MAX_LENGTH = 2048;
const ASSET_URL = /^https?:\/\/\S+$/i;

/** The key two chips collide on — trimmed and lower-cased, as `ChipsInput` and the backend
 * compare them. */
export const chipKey = (chip: string): string => chip.trim().toLowerCase();

export const defaultValueOf = (setting: ManifestSetting | ManifestChildSetting): unknown => {
  if (setting.control === 'composite' && 'children' in setting && setting.children?.length) {
    return Object.fromEntries(setting.children.map((child) => [child.name, defaultValueOf(child)]));
  }
  return 'default' in setting ? setting.default : undefined;
};

/** The value shown on screen: the stored override, or the manifest default when the
 * tenant has never saved this setting. Overrides-only storage is what lets the two
 * be told apart at all. */
export const effectiveValueOf = (
  setting: ManifestSetting | ManifestChildSetting,
  overrides: ResolveConfigOverrides,
): unknown => {
  const hasOverride = Object.prototype.hasOwnProperty.call(overrides, setting.name);
  if (!hasOverride) return defaultValueOf(setting);
  const override = overrides[setting.name];
  if (setting.control === 'composite' && 'children' in setting && setting.children?.length && typeof override === 'object' && override !== null) {
    // A composite override may itself be partial (only some children ever saved).
    return Object.fromEntries(
      setting.children.map((child) => [
        child.name,
        Object.prototype.hasOwnProperty.call(override as object, child.name)
          ? (override as Record<string, unknown>)[child.name]
          : defaultValueOf(child),
      ]),
    );
  }
  return override;
};

export const dependsOnSatisfied = (
  setting: ManifestSetting,
  overrides: ResolveConfigOverrides,
  settingsByName: Map<string, ManifestSetting>,
): boolean => {
  if (!setting.depends_on) return true;
  const target = settingsByName.get(setting.depends_on.setting);
  if (!target) return true;
  return effectiveValueOf(target, overrides) === setting.depends_on.equals;
};

/** A manifest bound (`options.min`/`options.max`) only when it is a whole number — `null`
 * or a missing key means "no bound". The backend's `_int_bound` accepts the same values. It
 * treats anything else (e.g. `"4"`) as a broken manifest and fails the save, so ignoring it
 * here only defers that error to Save rather than hiding it. */
export const intBoundOf = (bound: unknown): number | undefined =>
  Number.isInteger(bound) ? (bound as number) : undefined;

/** Why a `chips` list cannot be saved, or `null` — the same rules, in the same order, as the
 * backend's `_chips_error` (`Resolve/resolve_admin.py`), whose 400 rejects the WHOLE PUT. A
 * stored list can already break them on load (saved before a bound existed), so
 * `ResolveConfigContext` derives this for every list rather than waiting for an edit. */
export const chipsError = (setting: ManifestSetting | ManifestChildSetting, value: unknown): string | null => {
  if (setting.control !== 'chips') return null;
  if (!Array.isArray(value)) return 'This must be a list — remove the stored value and add items again.';
  if (!value.length && 'required' in setting && setting.required) return `${setting.title} is required.`;
  const empty = value.filter((item) => typeof item !== 'string' || !item.trim()).length;
  if (empty) return `Remove ${empty} empty ${empty === 1 ? 'item' : 'items'}.`;
  const items = value as string[];
  const long = items.filter((item) => item.length > CHIP_MAX_LENGTH).length;
  if (long) return `Shorten ${long} ${long === 1 ? 'item' : 'items'} — at most ${CHIP_MAX_LENGTH} characters each.`;
  const seen = new Set<string>();
  const repeated = items.find((item) => {
    if (seen.has(chipKey(item))) return true;
    seen.add(chipKey(item));
    return false;
  });
  if (repeated !== undefined) return `Remove the repeated "${repeated.trim()}".`;
  const max = intBoundOf(setting.options?.max);
  if (max !== undefined && value.length > max) return `Remove ${value.length - max} — at most ${max} allowed.`;
  const min = intBoundOf(setting.options?.min);
  if (min !== undefined && value.length < min) return `Add ${min - value.length} — at least ${min} required.`;
  return null;
};

/** The allowed values of a `radio` / `dropdown` / `checkbox` / `multi_select` setting. */
const optionValuesOf = (setting: ManifestSetting | ManifestChildSetting): unknown[] =>
  (setting.options?.values ?? []).map((option) => (typeof option === 'object' && option !== null ? option.value : option));

/** Strict (`===`) membership, so `true` never matches `1` — as the backend's `_same_value`. */
const isOption = (setting: ManifestSetting | ManifestChildSetting, value: unknown): boolean =>
  optionValuesOf(setting).some((option) => option === value);

const textError = (value: unknown, max: number): string | null => {
  if (typeof value !== 'string') return 'Enter text.';
  return value.length > max ? `At most ${max} characters (${value.length} now).` : null;
};

const numberError = (setting: ManifestSetting | ManifestChildSetting, value: unknown): string | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Enter a number.';
  // The backend refuses an integer Mongo cannot store (int64); JS is exact only this far.
  if (Number.isInteger(value) && Math.abs(value) > Number.MAX_SAFE_INTEGER) return 'Enter a smaller number.';
  const min = intBoundOf(setting.options?.min);
  if (min !== undefined && value < min) return `At least ${min}.`;
  const max = intBoundOf(setting.options?.max);
  if (max !== undefined && value > max) return `At most ${max}.`;
  return null;
};

const assetError = (value: unknown): string | null => {
  if (typeof value !== 'string') return 'Enter an image URL.';
  if (value.length > ASSET_MAX_LENGTH) return `At most ${ASSET_MAX_LENGTH} characters.`;
  return ASSET_URL.test(value) ? null : 'Enter an http(s) image URL.';
};

const optionListError = (setting: ManifestSetting | ManifestChildSetting, value: unknown): string | null => {
  if (!Array.isArray(value)) return 'Pick from the list.';
  if (value.some((item) => !isOption(setting, item))) return 'Remove the value that is not one of the options.';
  return new Set(value).size === value.length ? null : 'Remove the repeated value.';
};

/** A composite's value is an object keyed by child name; it may be partial. Every key must be a
 * child, and every child value is checked by its own control. */
const compositeError = (setting: ManifestSetting | ManifestChildSetting, value: unknown): string | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return 'This setting is stored in the wrong shape.';
  const children = new Map(('children' in setting ? setting.children ?? [] : []).map((child) => [child.name, child]));
  for (const [name, childValue] of Object.entries(value as Record<string, unknown>)) {
    const child = children.get(name);
    if (!child) return `"${name}" is not part of this setting.`;
    const error = validateValue(child, childValue);
    if (error) return `${child.title}: ${error}`;
  }
  return null;
};

/** Inline validation, `null` meaning valid: the backend PUT's rules (`_setting_error`,
 * `Resolve/resolve_admin.py`) for every control, so a value the backend would reject is flagged
 * on the row before Save rather than failing the whole save. `null` (and `''` for the free-text
 * controls) resets a setting to its default and is valid unless the setting is `required`. */
export const validateValue = (setting: ManifestSetting | ManifestChildSetting, value: unknown): string | null => {
  if (setting.control === 'chips') return chipsError(setting, value);
  const blank = value === null || value === undefined
    || (value === '' && ['text', 'textarea', 'colour', 'asset'].includes(setting.control));
  if (blank) return 'required' in setting && setting.required ? `${setting.title} is required.` : null;
  switch (setting.control) {
    case 'text':
      return textError(value, TEXT_MAX_LENGTH);
    case 'textarea':
      return textError(value, TEXTAREA_MAX_LENGTH);
    case 'colour':
      return typeof value === 'string' && HEX_COLOUR.test(value) ? null : 'Enter a valid hex colour, e.g. #2D5A27.';
    case 'asset':
      return assetError(value);
    case 'number':
      return numberError(setting, value);
    case 'toggle':
      return typeof value === 'boolean' ? null : 'Turn this on or off.';
    case 'radio':
    case 'dropdown':
      return isOption(setting, value) ? null : 'Pick one of the options.';
    case 'checkbox':
    case 'multi_select':
      return optionListError(setting, value);
    case 'composite':
      return compositeError(setting, value);
    default:
      return null;
  }
};
